// Phase C.5a — pure event-scope preservation helpers for Add/Edit Event's
// Practice/Other half (components/teams/GameFormDialogs.js). Extracted
// after a browser-found bug: the dialog's submit logic gated whether it
// sent `scopeType`/`teamIds`/`divisionId` back to the server behind
// whether the "Applies To" picker was VISIBLE (a Team Schedule context
// never shows it), rather than behind what the event's ACTUAL current
// scope already was. An inherited entire-Division/specific-Teams event
// opened from a Team's own Schedule would silently submit with no scope
// override at all, and the server-side fallback to the existing
// scope_type then had no membership/division data to go with it —
// correctly rejected ("At least one Team is required"). These functions
// are the one place that decision is made now, independent of any UI
// visibility concern, so this exact bug class can't recur silently.

/** Initializes applyScope/specificTeamIds from an EXISTING event's own
 * current scope — used whenever the Add/Edit Event dialog opens to edit
 * something, regardless of whether the "Applies To" picker will even be
 * shown for this context. A plain Team-scoped event (or a legacy row
 * with no scope_type at all) has no third "Team" radio in the UI, so it
 * is represented as Specific Teams: just that one Team — a coherent,
 * still-editable state rather than an invisible option. */
export function initEventScopeFromExisting(event) {
  if (event.scope_type === 'entire_division') {
    return { applyScope: 'entire_division', specificTeamIds: [] };
  }
  if (event.scope_type === 'specific_teams') {
    return { applyScope: 'specific_teams', specificTeamIds: event.scope_team_ids || [] };
  }
  return { applyScope: 'specific_teams', specificTeamIds: event.ownerTeamId ? [event.ownerTeamId] : [] };
}

/** Null when the current scope selection is submittable; otherwise a
 * user-facing error string. Deliberately takes no "is the picker
 * visible" input — an inherited event's scope must be validated (and
 * preserved) the same way whether or not the coach can see/change it in
 * this particular context. */
export function validateEventScope({ applyScope, specificTeamIds, divisionId, event }) {
  if (applyScope === 'specific_teams' && (!specificTeamIds || specificTeamIds.length === 0)) {
    return 'Choose at least one Team.';
  }
  if (applyScope === 'entire_division' && !(divisionId || event?.division_id)) {
    return 'This Division could not be determined — try reopening the dialog.';
  }
  return null;
}

/** Resolves exactly what createTeamEvent()/updateTeamEvent() should
 * receive for the event's scope — the ONLY place this decision is made,
 * driven purely by `applyScope` (already correctly preloaded from the
 * event's own current scope by initEventScopeFromExisting() above,
 * whether or not the "Applies To" UI is visible in this context).
 * `divisionId` falls back to the event's own `division_id` when the
 * dialog wasn't handed one as a prop — Team Schedule never passes one;
 * only the Division Hub does. */
export function resolveEventScopePayload({ applyScope, specificTeamIds, divisionId, event, effectiveTeamId }) {
  if (applyScope === 'entire_division') {
    return { scopeType: 'entire_division', divisionId: divisionId || event?.division_id || null };
  }
  if (applyScope === 'specific_teams') {
    return { scopeType: 'specific_teams', teamIds: specificTeamIds || [] };
  }
  return { teamId: effectiveTeamId };
}
